import { Button, Card, Checkbox, Col, Modal, Radio, Row, Select } from 'antd';
import { Column, dataIndexExportKeyMap } from './tableCol';
import { useCallback, useMemo, useState } from 'react';
import posthog from '@leyan/lytics';
import { useLocalStorageState } from 'ahooks';
import { downloadAssistantPerf } from 'services/api/dashboard';
import { useDashboardCurrency } from 'views/Dashboard/ImplementData/hooks/useCurrency';
import FlexStyled from 'components/styled/flex';
import { momentDateRange2FetchParams } from 'views/Dashboard/ImplementData/utils';
import { FilterCtxType } from './Filter';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import { useTranslate } from '@tolgee/react';
import { useExportFile } from 'components/ExportCenter/hooks/useExportCenter';
import { DepartmentsSelect } from 'views/Dashboard/ImplementData/components/Filter/DepartmentSelect';
import { useCustomSelect } from './Content';
import dayjs, { Dayjs } from 'lib/dayjs';
import { DatePicker } from 'components/DatePicker';
import { currencyOptions } from 'views/Dashboard/ImplementData/components/Filter/CurrencySelector';

interface Props {
  allColumns: Column[];
  showColumns: Column[];
  sortedColumns: Column[];
  fixedColumns: Column[];
  initialValue: FilterCtxType;
  onClose: () => void;
}

const ExportPrefModal = (props: Props) => {
  const { allColumns, showColumns, onClose, sortedColumns, fixedColumns, initialValue } = props;
  const { t } = useTranslate();

  const indicatorList = useMemo(
    () => [
      {
        label: t('export_pref_modal_all_data_indicator'),
        value: '整体数据指标',
      },
      {
        label: t('export_pref_modal_daily_data_indicator'),
        value: '天级数据指标',
      },
      {
        label: t('export_pref_modal_department_indicator'),
        value: '分部门数据指标',
      },
      {
        label: t('export_perf_modal_daily_assistant_online_data'),
        value: '天级客服在线数据',
      },
    ],
    [t],
  );
  const [checkedIndicators, setCheckedIndicators] = useLocalStorageState(
    `@fett/export-pref-indicator-sheet-v2`,
    {
      defaultValue: indicatorList.map((x) => x.value),
    },
  );
  const [indicatorType, setIndicatorType] = useLocalStorageState(
    '@fett/export-pref-indicator-type-v2',
    {
      defaultValue: 'checked',
    },
  );
  const { currency } = useDashboardCurrency();
  const lang = useSystemLanguageValue();

  const [filter, setFilter] = useState({ ...initialValue, currency });

  const params = useMemo(() => {
    const { dateRange, departments, ...rest } = filter;
    return {
      departmentIds: departments?.map((_) => _.id),
      ...rest,
      ...momentDateRange2FetchParams(dateRange),
      // 产品在提测以后加的需求，上线就被老板砍了
      conversationType: 2 as const,
    };
  }, [filter]);

  const { run, loading } = useExportFile();

  const onDownload = useCallback(
    (indexes?: string[]) => {
      const { currency, ...rest } = params;
      run(() =>
        downloadAssistantPerf({
          ...rest,
          currency: currency?.shorthand === 'CNY' ? 'RMB' : currency?.shorthand,
          language: lang,
          indexes,
          sheets: checkedIndicators ?? [],
        }).then(() => onClose?.()),
      );
    },
    [params, run, lang, checkedIndicators, onClose],
  );

  const onExport = useCallback(
    (key) => {
      if (key === 'all') {
        onDownload();
      }
      if (key === 'checked') {
        const indexes = sortedColumns.reduce<string[]>((res, cur) => {
          const { show, dataIndex } = cur;

          if (!show) {
            return res;
          }

          const exportKey = dataIndexExportKeyMap[dataIndex as keyof typeof dataIndexExportKeyMap];

          if (exportKey) {
            res.push(exportKey);
          }

          return res;
        }, []);

        onDownload(indexes);
      }
      posthog.capture('$assistantPerformance', {
        type: 'downloadGather',
        key,
      });
    },
    [onDownload, sortedColumns],
  );

  const departmentSelect = (
    <DepartmentsSelect
      dashboardType="performance"
      value={filter.departments}
      onChange={(departments) => {
        setFilter((pre) => ({
          ...pre,
          departments,
          assistantIds: undefined,
        }));
      }}
    />
  );

  const { customSelect } = useCustomSelect({
    departments: filter.departments,
    value: filter.assistantIds,
    onChange: (value) => {
      setFilter((pre) => ({ ...pre, assistantIds: value }));
    },
  });

  return (
    <Modal
      onCancel={onClose}
      visible
      width={600}
      title={t('export_pref_modal_export_perf_form')}
      footer={
        <FlexStyled.Flex className="justify-end">
          <span className="mr-4">
            {t('export_pref_modal_selected_x', { x: checkedIndicators?.length ?? 0 })}
          </span>
          <Button type="default" disabled={loading} onClick={onClose}>
            {t('translation.取消')}
          </Button>
          <Button
            type="primary"
            onClick={() => onExport(indicatorType)}
            loading={loading}
            disabled={!checkedIndicators?.length}
          >
            {t('export_pref_modal_button_export')}
          </Button>
        </FlexStyled.Flex>
      }
    >
      <Card
        className="!mb-4"
        title={
          <div className="flex gap-4">
            {departmentSelect}
            {(filter.departments || []).length > 0 && customSelect}
            <Select
              value={filter.currency?.region}
              options={currencyOptions}
              onChange={(value) => {
                const currency = currencyOptions.find((_) => _.region === value);
                setFilter((pre) => ({
                  ...pre,
                  currency: currency!,
                }));
              }}
            />
          </div>
        }
      >
        <span className="mr-4">{t('ViewDashboard.日期')}</span>
        <DatePicker.RangePicker
          className="w-[380px]"
          disabledDate={(current) => {
            return current && (current < dayjs().subtract(1, 'year') || current > dayjs());
          }}
          value={filter.dateRange}
          onChange={(dateRange) => {
            setFilter((pre) => ({ ...pre, dateRange: dateRange as [Dayjs, Dayjs] }));
          }}
        />
      </Card>
      <Card
        title={
          <Checkbox
            className="flex"
            checked={checkedIndicators?.length === indicatorList.length}
            indeterminate={
              checkedIndicators &&
              checkedIndicators?.length > 0 &&
              checkedIndicators?.length < indicatorList.length
            }
            onChange={(e) =>
              setCheckedIndicators(e.target.checked ? indicatorList.map((x) => x.value) : [])
            }
          >
            {t('export_perf_modal_select_all')}
          </Checkbox>
        }
      >
        <Checkbox.Group
          onChange={(list) => setCheckedIndicators(list as string[])}
          value={checkedIndicators}
        >
          <Row gutter={[16, 16]}>
            {indicatorList.map((item) => (
              <Col span={12} key={item.value}>
                <Checkbox value={item.value}>{item.label}</Checkbox>
              </Col>
            ))}
          </Row>
        </Checkbox.Group>
      </Card>
      <div className="mt-3 flex gap-1 justify-start">
        {t('export_pref_modal_export_options')}:
        <Radio.Group value={indicatorType} onChange={(e) => setIndicatorType(e.target.value)}>
          <Radio value="checked">
            {t('export_perf_modal_export_selected_x_indictors', {
              x: showColumns.length - fixedColumns.length,
            })}
          </Radio>
          <Radio value="all">
            {t('export_pref_modal_export_all_x_indoctors', {
              x: allColumns.length - fixedColumns.length,
            })}
          </Radio>
        </Radio.Group>
      </div>
    </Modal>
  );
};

export default ExportPrefModal;
